import React from 'react';
import { Group, Row } from '@common/components/form';
import ModalContent from '@common/components/modal/modal-content';
import ModalWindow from '@common/components/modal/modal-window';
import { useAppSelector } from '@common/hooks';
import { selected } from '@modules/organisation/selectors/organisation';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { updateCategory } from '@modules/performance/action/update-category';
import { createCategory } from '@modules/performance/action/create-category';
import { AlertService } from '@common/services/alert';
import { TextInputHookForm } from '@common/components/form/inputs/text';

const CategoryModal = ({
  category,
  setCompetency,
  onClose,
}: {
  category?: any;
  setCompetency: (competency: any) => void;
  onClose: () => void;
}) => {
  const { t } = useTranslation();
  const organisation = useAppSelector(selected);
  const isEditMode = !!category;

  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm({
    defaultValues: {
      name: category?.group?.name?.string || category?.group?.name || '',
    },
  });

  const onSubmit = async (data: { name: string }) => {
    try {
      if (isEditMode) {
        await updateCategory(organisation.id, category.group.id, {
          name: data.name,
        });

        setCompetency((prev: any) => prev.map((item: any) => (item.group.id === category.group.id
              ? {
                  ...item,
                  group: {
                    ...item.group,
                    name: { string: data.name }
                  },
                }
              : item)
          )
        );

        AlertService.success(t('performance:category_updated_successfully'));
      } else {
        const response = await createCategory(organisation.id, {
          name: data.name,
        });
        setCompetency((prev: any) => [...prev, {
            group: response.data,
            groupType: 'category',
            competencies: [],
        }]);

        AlertService.success(t('performance:category_created_successfully'));
      }

      onClose();
    } catch (error: any) {
      AlertService.forStatus(error.status_code, {
        warning: isEditMode
          ? t('performance:category_update_warning')
          : t('performance:category_create_warning'),
        error: isEditMode
          ? t('performance:category_update_error')
          : t('performance:category_create_error'),
      });
    }
  };

  return (
    <ModalWindow show onHide={onClose} size="small" className="EditableModule">
      <ModalContent
        title={isEditMode ? t('performance:edit_category') : t('performance:add_category')}
        onHide={onClose}
        onConfirm={handleSubmit(onSubmit)}
        confirmButtonText={isEditMode ? t('common:save_changes') : t('performance:create_category')}
        confirmButtonLoading={isSubmitting}
      >
        <Row>
          <Group>
            <label className="!tw-text-sm !tw-font-medium !tw-text-gray-textDark">
              {t('performance:name')}
            </label>
            <TextInputHookForm
              className="tw-text-base tw-font-inter tw-font-medium tw-mt-1"
              name="name"
              control={control}
              placeholder={t('performance:enter_category_name')}
            />
          </Group>
        </Row>
      </ModalContent>
    </ModalWindow>
  );
};

export default CategoryModal;
